import { useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@archipro/rocco/components/button';
import {
    ClientDirectorBookingPanel,
    type BookingPrefill,
} from '~/modules-rocco/booking';

interface EnquiryBookingStepProps {
    /** Booking link the enquiry was routed to. */
    sessionId: number;
    /** The enquiry that was just created, so the booking is linked to it. */
    enquiryId: number | null;
    prefill: BookingPrefill;
    onClose: () => void;
}

/**
 * Second step of an enquiry that routed to a booking link: the details have
 * been sent, now the enquirer picks a time. Rendered in place of the form.
 */
export const EnquiryBookingStep = ({
    sessionId,
    enquiryId,
    prefill,
    onClose,
}: EnquiryBookingStepProps) => {
    const [booked, setBooked] = useState(false);
    const extraFormFields = useMemo(
        () => (enquiryId ? { enquiryId: String(enquiryId) } : undefined),
        [enquiryId],
    );

    return (
        <div className="flex flex-col gap-4" aria-live="polite">
            <div className="flex items-start gap-3 rounded-lg bg-gray-50 px-4 py-3">
                <CheckCircle2 className="text-green-600 mt-0.5 size-5 shrink-0" />
                <div className="text-sm">
                    <div className="font-medium text-gray-900">
                        Details sent
                    </div>
                    <div className="text-gray-600">
                        {booked
                            ? 'Your meeting is booked. A confirmation is on its way to your email.'
                            : 'Now pick a time that suits you to book your meeting.'}
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200">
                <ClientDirectorBookingPanel
                    sessionId={sessionId}
                    active
                    prefill={prefill}
                    extraFormFields={extraFormFields}
                    onBooked={() => setBooked(true)}
                />
            </div>
            <div className="flex justify-end">
                <Button
                    color={booked ? undefined : 'white'}
                    weight="normal"
                    className="px-6 py-2.5"
                    onClick={onClose}
                >
                    {booked ? 'Done' : 'Skip for now'}
                </Button>
            </div>
        </div>
    );
};
